<template>
  <main class="p-userauthentication">
    <section class="p-userauthentication__wrap">
      <h2 class="c-regist__title">{{ t('AuthenticateUserPage.accountResist') }}</h2>
      <RegisterFlow :steps="6" :nowStep="2" />
      <p class="c-regist__comment">{{ t('AuthenticateUserPage.registMessage') }}</p>
      <div
        :class="[
          registerTeamStore.isLineRegistrationVisible
            ? 'p-userauthentication__contents'
            : 'p-userauthentication-lineregistrationvisible__contents',
        ]"
      >
        <div
          class="p-userauthentication__content"
          v-if="registerTeamStore.isLineRegistrationVisible"
        >
          <p class="p-userauthentication__content-title">
            {{ t('AuthenticateUserPage.registerWithSNS') }}
          </p>
          <div class="p-userauthentication__line">
            <a class="p-userauthentication__line-link" @click="registerByLine">{{
              t('AuthenticateUserPage.registerWithLine')
            }}</a>
          </div>
        </div>
        <div
          :class="[
            registerTeamStore.isLineRegistrationVisible
              ? 'p-userauthentication__content'
              : 'p-userauthentication-lineregistrationvisible__content',
          ]"
        >
          <div
            class="p-userauthentication__content-separation"
            v-if="registerTeamStore.isLineRegistrationVisible"
          >
            {{ t('AuthenticateUserPage.or') }}
          </div>
          <p
            class="p-userauthentication__content-title"
            v-if="registerTeamStore.isLineRegistrationVisible"
          >
            {{ t('AuthenticateUserPage.registerWithMail') }}
          </p>
          <p
            :class="[
              registerTeamStore.isLineRegistrationVisible
                ? 'p-userauthentication__form-title'
                : 'p-userauthentication-lineregistrationvisible__form-title',
            ]"
          >
            {{ t('AuthenticateUserPage.mail') }}
          </p>
          <input-text
            ref="inFieldMailAddress"
            v-model:input-value="registerTeamStore.mailAddress"
            class="p-userauthentication__form-text"
            name="mailAddress"
            placeholder="sample@ex.co.jp"
            rules="required|emailFormat"
            :otherErrorMessage="
              state.isDuplicateMailAddress
                ? [t('AuthenticateUserPage.alreadyInUseMailAddressMessage')]
                : []
            "
            @input="
              () => {
                state.isAvailableMailAddress = false
                state.isDuplicateMailAddress = false
              }
            "
            @delayinput="checkAvailableMailAddress"
          />
          <!-- ↓ エラーメッセージ / 「-hidden」クラスの付け外しで表示非表示切り替え -->
          <p class="p-userauthentication__error -hidden">
            {{ t('AuthenticateUserPage.error.duplicateMail') }}
          </p>
          <!-- ↑ エラーメッセージ / 「-hidden」クラスの付け外しで表示非表示切り替え -->
          <div class="p-userauthentication__recaptcha">
            <!-- ↓ reCAPTCHA -->
            <!-- ↑ reCAPTCHA -->
          </div>
          <AuthUserNote />
          <div
            :class="[
              registerTeamStore.isLineRegistrationVisible
                ? 'p-userauthentication__link'
                : 'p-userauthentication-lineregistrationvisible__link',
            ]"
          >
            <button
              v-on:click="onClickSendMail"
              :disabled="!isEnableButton"
              :class="{ isDisabled: !isEnableButton }"
              class="c-button"
            >
              <span class="c-button__text">{{ t('AuthenticateUserPage.sendMail') }}</span>
            </button>
          </div>
        </div>
      </div>
      <AuthUserComment />
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, computed, reactive, onMounted, ref, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import RegisterFlow from '@/components/registerTeam/RegisterFlow.vue'
import AuthUserNote from '@/views/AuthenticateUserPage/components/AuthUserNote.vue'
import AuthUserComment from '@/views/AuthenticateUserPage/components/AuthUserComment.vue'
import StoreUtil from '@/stores/StoreUtil'
import useValidateForAccess from '@/Hooks/useValidateForAccess'
import InputText from '@/components/common/field/InputText.vue'
import { AxiosError } from 'axios'

export default defineComponent({
  name: 'AuthenticateUserPage',
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const registerTeamStore = StoreUtil.useStore('RegisterTeamStore')
    const inFieldMailAddress = ref<InstanceType<typeof InputText>>()
    const state = reactive({
      isSentMail: false,
      isAvailableMailAddress: false,
      isDuplicateMailAddress: false,
    })
    const isEnableButton = computed(
      () =>
        inFieldMailAddress.value?.meta.valid &&
        state.isAvailableMailAddress &&
        !state.isSentMail &&
        !state.isDuplicateMailAddress,
    )
    const messageDialogStore = StoreUtil.useStore('MessageDialogStore')

    /**
     * 利用可能なメールアドレスかどうか判定する。
     */
    const checkAvailableMailAddress = async () => {
      state.isAvailableMailAddress = false
      await nextTick()
      if (!inFieldMailAddress.value?.value) return
      await inFieldMailAddress.value?.validate()
      if (!inFieldMailAddress.value?.meta.valid) return
      // メールアドレスの重複チェックを実行する
      const result = await registerTeamStore.checkDuplicateMailAllRegion()
      if (result.isValidMainAddress) {
        state.isDuplicateMailAddress = false
        state.isAvailableMailAddress = true
      } else if (result.isDuplicateMail) {
        state.isDuplicateMailAddress = true
        state.isAvailableMailAddress = false
      } else {
        // ネットワークエラーなどでチェックができなかったときの処理
        state.isDuplicateMailAddress = false
        state.isAvailableMailAddress = false
      }
    }

    const openRegisterTokenErrorMessageDialog = async (submitResult: AxiosError) => {
      let dialogOptions = {}
      switch (submitResult.response?.data.error_code) {
        case '400_0001': {
          dialogOptions = {
            title: t('AuthenticateUserPage.parameterInvalid.title'),
            message: t('AuthenticateUserPage.parameterInvalid.message'),
            status: 'error',
          }
          break
        }
        case '400_0139': {
          dialogOptions = {
            title: t('AuthenticateUserPage.failedToCreateMultipleOrganizations.title'),
            message: t('AuthenticateUserPage.failedToCreateMultipleOrganizations.message'),
            status: 'error',
          }
          break
        }
        case '500_0018': {
          dialogOptions = {
            title: t('AuthenticateUserPage.failedToSendMail.title'),
            message: t('AuthenticateUserPage.failedToSendMail.message'),
            status: 'error',
          }
          break
        }
        case 'NETWORK_ERROR': {
          dialogOptions = {
            title: t('dialog.netWorkError.title'),
            message: t('dialog.netWorkError.message'),
            status: 'error',
          }
          break
        }
        default: {
          dialogOptions = {
            title: t('dialog.unknownError.title'),
            message: t('dialog.unknownError.message', [submitResult.response?.data.error_code]),
            status: 'error',
          }
          break
        }
      }
      await messageDialogStore.openWithSettings(dialogOptions)
    }
    /**
     * LINEで登録する。
     */
    const registerByLine = async () => {
      const submitResult = await registerTeamStore.getTokenAndSendCreateUserRequest('SNS_LINE')
      if (!submitResult.isSuccess) {
        openRegisterTokenErrorMessageDialog(submitResult.res)
        return
      }
      const stateParamStr = submitResult.res.data.tokenId
      const redirectUri = process.env.VUE_APP_LINE_REDIRECT_URI.replace('#', '%23')
      window.location.href = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${process.env.VUE_APP_LINE_CLIENT_ID}&redirect_uri=${redirectUri}&state=${stateParamStr}&bot_prompt=aggressive&scope=profile%20openid`
    }
    onMounted(() => {
      checkAvailableMailAddress()
      useValidateForAccess('RegisterTeamStore', ['teamName', 'organizationType'], 'ErrorPage')
    })
    return {
      state,
      registerTeamStore,
      isEnableButton,
      checkAvailableMailAddress,
      t,
      inFieldMailAddress,
      registerByLine,
      /**
       * メール送信ボタンを押したときの処理
       */
      async onClickSendMail() {
        state.isSentMail = true
        const submitResult = await registerTeamStore.getTokenAndSendCreateUserRequest(
          'MAIL_ADDRESS',
        )
        if (submitResult.isSuccess) {
          router.replace({ name: 'SendMailCompletelyPage' })
        } else {
          openRegisterTokenErrorMessageDialog(submitResult.res)
          state.isSentMail = false
        }
      },
    }
  },
  components: {
    RegisterFlow,
    AuthUserNote,
    AuthUserComment,
    InputText,
  },
})
</script>

<style scoped lang="scss">
.isDisabled {
  opacity: 0.3;
  pointer-events: none;
}
.p-userauthentication__line-link {
  cursor: pointer;
}
</style>
